import {Component, computed, inject, Injector, signal} from "@angular/core";
import {BasicMaterialDialog, MatDialogDataBaseInput} from "../../basic-material-dialog/basic-material-dialog.directive";
import {CompetenceCategoryEntry, UserRecordingAssessment} from "@models";
import {NonNullableFormBuilder} from "@angular/forms";
import {ChartConfiguration, ChartData, DefaultDataPoint} from "chart.js";
import {toSignal} from "@angular/core/rxjs-interop";
import {startWith} from "rxjs/operators";
import {derivedAsync} from "ngxtension/derived-async";
import {SimAssessmentScoreService, SimAssessmentService} from "@services";
import {EnumLabel} from "../../../../../shared/shared-pipes/enum-label.pipe";

type EvaluatedCompetenceCategories = Record<string, CompetenceCategoryEntry>;
type EvaluatedCompetenceCategoriesByAssessmentId = Record<string, EvaluatedCompetenceCategories>;

export interface DialogData_CompetenceCategoryGraphDialogInput extends MatDialogDataBaseInput {
	assessments: UserRecordingAssessment[];
	stationIndex: number;
	examinerName?: string;
	studentName: string;
	scenarioName: string;
}

@Component({
	selector: "sim-competence-category-graph-dialog",
	templateUrl: "./competence-category-graph-dialog.component.html",
	styleUrl: "./competence-category-graph-dialog.component.scss",
	standalone: false,
})
export class CompetenceCategoryGraphDialogComponent extends BasicMaterialDialog<
	CompetenceCategoryGraphDialogComponent,
	DialogData_CompetenceCategoryGraphDialogInput
> {
	protected readonly EnumLabel = EnumLabel;

	readonly #nnfb = inject(NonNullableFormBuilder);
	readonly #assessmentService = inject(SimAssessmentService);

	protected readonly assessmentControl = this.#nnfb.control(this.data.assessments[0]);

	constructor(_injector: Injector) {
		super(_injector);
	}

	// Exposed signals
	protected readonly reachedScore = computed(
		() => this.#selectedAssessment().reachedScore || this.#selectedAssessment().reachedScoreTemporary,
	);
	protected readonly reachedScorePercentage = computed(() => SimAssessmentScoreService.getScorePercentage(this.#selectedAssessment()));
	protected readonly maxScore = computed(() => this.#selectedAssessment().maxScore);
	protected readonly state = computed(() => SimAssessmentScoreService.getAssessmentGrade(this.#selectedAssessment()));

	protected readonly chartOptions = computed<ChartConfiguration<"radar", DefaultDataPoint<"radar">, any>["options"]>(() =>
		computeChartOptions(this.#selectedAssessment()),
	);
	protected readonly chartData = computed<ChartData<"radar", DefaultDataPoint<"radar">, any> | undefined>(() =>
		computeChartData(this.#selectedDistribution()),
	);

	// Private signals
	readonly #selectedAssessment = toSignal(this.assessmentControl.valueChanges.pipe(startWith(this.assessmentControl.value)), {
		requireSync: true,
	});
	readonly #assessments = signal(this.data.assessments).asReadonly();
	readonly #distributions = derivedAsync(async () => {
		const assessments = this.#assessments();
		const ids = assessments.map((assessment) => assessment.id);
		return await this.#assessmentService.getCompetenceCategoryDistributionByIds(ids);
	});
	readonly #selectedDistribution = computed(() =>
		computeEvaluatedCompetenceCategories(this.#selectedAssessment(), this.#distributions()),
	);
}

function computeEvaluatedCompetenceCategories(
	assessment: UserRecordingAssessment,
	categoriesByAssessmentId: EvaluatedCompetenceCategoriesByAssessmentId | undefined,
): EvaluatedCompetenceCategories | undefined {
	if (!categoriesByAssessmentId) {
		return undefined;
	}
	return categoriesByAssessmentId[assessment.id];
}

function computeChartOptions(assessment: UserRecordingAssessment): ChartConfiguration<"radar", DefaultDataPoint<"radar">, any>["options"] {
	return {
		scales: {
			r: {min: 0, max: 100},
		},
		plugins: {
			title: {
				display: true,
				text: assessment.name,
				position: "top",
				align: "start",
			},
			legend: {display: false},
		},
	};
}

function computeChartData(
	evaluatedCompetenceCategories: EvaluatedCompetenceCategories | undefined,
): ChartData<"radar", DefaultDataPoint<"radar">, any> | undefined {
	if (!evaluatedCompetenceCategories) {
		return undefined;
	}

	const reachedCompetenceCategories = Object.values(evaluatedCompetenceCategories);

	const labels = reachedCompetenceCategories.map((value) => value.name);
	const data = reachedCompetenceCategories.map((value) => Math.round((value.reachedPercentage / value.maxPercentage) * 100));
	const colors = reachedCompetenceCategories.map((value) => value.color);

	return {
		labels,
		datasets: [
			{
				data,
				pointBorderColor: "#fff",
				pointBackgroundColor: colors,
				pointHoverBackgroundColor: "#fff",
				pointHoverBorderColor: colors,
				backgroundColor: "rgba(220, 220, 220, 0.4)",
				borderColor: "rgba(209, 209, 209, 1)",
				pointRadius: 5,
			},
		],
	};
}
